import React, {useEffect, useState} from 'react';
import {App, Button, Form, Input, InputNumber, Modal, Space, Switch} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {agentConfigService} from '../../services/agentConfigService';

const {TextArea} = Input;

interface GreetingConfigModalProps {
    visible: boolean;
    agentId: number;
    agentName: string;
    onClose: () => void;
    onSaved: () => void;
}

interface GreetingConfig {
    greetingMessage: string;
    exampleQuestions: string[];
    isActive: boolean;
}

const DEFAULT_GREETING = '你好！我是你的 AI 助手';
const DEFAULT_EXAMPLE = ['什么是夏普比率？', '为什么我的回测在实盘表现更差？', '止损应该怎么设置？'];

const GreetingConfigModal: React.FC<GreetingConfigModalProps> = ({
                                                                     visible,
                                                                     agentId,
                                                                     agentName,
                                                                     onClose,
                                                                     onSaved
                                                                 }) => {
    const {message: messageApi} = App.useApp();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [exampleQuestions, setExampleQuestions] = useState<string[]>([...DEFAULT_EXAMPLE]);
    const [preview, setPreview] = useState<GreetingConfig>({
        greetingMessage: DEFAULT_GREETING,
        exampleQuestions: [...DEFAULT_EXAMPLE],
        isActive: true
    });

    useEffect(() => {
        if (visible && agentId) {
            loadGreetingConfig();
        }
    }, [visible, agentId]);

    const loadGreetingConfig = async () => {
        setLoading(true);
        try {
            const data = await agentConfigService.getAgentGreeting(agentId);
            if (data) {
                const msg = data.greetingMessage || DEFAULT_GREETING;
                const examples = data.exampleQuestions && data.exampleQuestions.length > 0
                    ? data.exampleQuestions
                    : [];
                const active = data.isActive !== 0;
                form.setFieldsValue({greetingMessage: msg, isActive: active});
                setExampleQuestions(examples);
                setPreview({greetingMessage: msg, exampleQuestions: examples, isActive: active});
            } else {
                form.resetFields();
                setExampleQuestions([]);
                setPreview({greetingMessage: DEFAULT_GREETING, exampleQuestions: [], isActive: true});
            }
        } catch {
            form.resetFields();
            setExampleQuestions([]);
            setPreview({greetingMessage: DEFAULT_GREETING, exampleQuestions: [], isActive: true});
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = () => {
        const values = form.getFieldsValue();
        setPreview({
            greetingMessage: values.greetingMessage || DEFAULT_GREETING,
            exampleQuestions: exampleQuestions,
            isActive: values.isActive !== false
        });
    };

    const addExample = () => {
        setExampleQuestions([...exampleQuestions, '']);
    };

    const removeExample = (idx: number) => {
        const updated = exampleQuestions.filter((_, i) => i !== idx);
        setExampleQuestions(updated);
        setPreview(prev => ({...prev, exampleQuestions: updated}));
    };

    const updateExample = (idx: number, value: string) => {
        const updated = [...exampleQuestions];
        updated[idx] = value;
        setExampleQuestions(updated);
        setPreview(prev => ({...prev, exampleQuestions: updated}));
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            const filteredExamples = exampleQuestions.filter(q => q.trim().length > 0);
            const submitData = {
                greetingMessage: values.greetingMessage,
                exampleQuestions: JSON.stringify(filteredExamples),
                isActive: values.isActive ? 1 : 0
            };
            await agentConfigService.saveAgentGreeting(agentId, submitData);
            messageApi.success('招呼语配置保存成功');
            onSaved();
            onClose();
        } catch (error: any) {
            if (error?.message) messageApi.error(error.message);
            else messageApi.error('保存失败');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            setSaving(true);
            await agentConfigService.deleteAgentGreeting(agentId);
            messageApi.success('招呼语配置已删除');
            onSaved();
            onClose();
        } catch (error: any) {
            if (error?.message) messageApi.error(error.message);
            else messageApi.error('删除失败');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={`招呼语配置 - ${agentName}`}
            open={visible}
            onCancel={onClose}
            width={720}
            footer={null}
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{greetingMessage: DEFAULT_GREETING, isActive: true}}
                onValuesChange={handleFieldChange}
            >
                <Form.Item name="greetingMessage" label="招呼语内容"
                           rules={[{required: true, message: '请输入招呼语'}]}>
                    <TextArea
                        rows={3}
                        placeholder="如：你好！我是你的 AI 助手"
                        maxLength={500}
                        showCount
                    />
                </Form.Item>

                <Form.Item label="示范问题">
                    {exampleQuestions.map((q, i) => (
                        <Space key={i} style={{display: 'flex', marginBottom: 8}} align="center">
                            <InputNumber
                                min={1}
                                max={99}
                                value={i + 1}
                                disabled
                                size="small"
                                style={{width: 48}}
                            />
                            <Input
                                value={q}
                                onChange={e => updateExample(i, e.target.value)}
                                placeholder="输入示范问题"
                                style={{width: 420}}
                                maxLength={200}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined/>}
                                onClick={() => removeExample(i)}
                                disabled={exampleQuestions.length <= 1}
                            />
                        </Space>
                    ))}
                    <Button type="dashed" icon={<PlusOutlined/>} onClick={addExample} size="small">
                        添加示范问题
                    </Button>
                </Form.Item>

                <Form.Item name="isActive" label="启用状态" valuePropName="checked">
                    <Switch checkedChildren="启用" unCheckedChildren="禁用"/>
                </Form.Item>
            </Form>

            {/* 实时预览 */}
            {preview.isActive && (
                <div style={{
                    marginTop: 16,
                    padding: '1rem',
                    background: '#0A0F1E',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.07)'
                }}>
                    <div style={{fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: 12}}>实时预览：</div>
                    <div style={{display: 'flex', gap: '12px', maxWidth: '680px'}}>
                        <div style={{
                            width: '36px', height: '36px', borderRadius: '50%',
                            background: 'rgba(240,180,41,0.12)', border: '1px solid rgba(240,180,41,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 800, color: 'var(--pt-primary)',
                            fontFamily: "'Syne', sans-serif", flexShrink: 0
                        }}>AI
                        </div>
                        <div>
                            <div style={{
                                background: '#0F172A', border: '1px solid rgba(255,255,255,0.07)',
                                borderRadius: '4px 14px 14px', padding: '1rem 1.25rem',
                                fontSize: '0.9rem', lineHeight: 1.75, color: 'var(--pt-text-primary)'
                            }}>
                                {preview.greetingMessage}
                                {preview.exampleQuestions.length > 0 && (
                                    <>
                                        <br/>
                                        你可以问我任何关于交易和市场的问题，比如：<br/>
                                        {preview.exampleQuestions.filter(q => q.trim()).map((q, i) => (
                                            <React.Fragment key={i}>
                                                <span style={{color: 'var(--pt-primary)'}}>「{q}」</span>
                                                {i < preview.exampleQuestions.filter(qq => qq.trim()).length - 1 &&
                                                    <br/>}
                                            </React.Fragment>
                                        ))}
                                    </>
                                )}
                            </div>
                            <div style={{
                                fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)',
                                marginTop: '0.4rem', paddingLeft: '0.25rem'
                            }}>
                                {new Date().toLocaleTimeString('zh-CN', {hour: '2-digit', minute: '2-digit'})}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 20}}>
                <Button danger onClick={handleDelete} loading={saving}>
                    删除配置
                </Button>
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" onClick={handleSave} loading={saving}>保存</Button>
                </Space>
            </div>
        </Modal>
    );
};

export default GreetingConfigModal;
